import React, { useState, useCallback } from "react";
import Dialog, { convertWidth } from "components/Dialog";
import Button from "@mui/material/Button";
import { Stack, styled } from "@mui/material";
import {
  IDialogTemplateProps,
  IDialogInfoProps,
  IDialogConfirmProps,
  IVariant,
  ITypeDialog,
  IDialogConfig,
  IDialogContext,
  IGetDialog,
  DialogContentProps,
} from "./types";

import useWindowSize from "hook/useWindowSize";

import { useTranslation } from "react-i18next";

import SaveFailed from "assets/image/SaveFailed.png";
import Back from "assets/image/back.png";
import Failed from "assets/image/Failed.png";
import Lab from "assets/image/lab.png";
import Share from "assets/image/share.png";
import LoadFailed from "assets/image/LoadFailed.png";
import UndefinedQr from "assets/image/undefined_qr.png";

const DialogContentDiv = styled(`div`)<DialogContentProps>`
  ${(props) => {
    if (props.size) {
      if (convertWidth(props.size) > props.screenwidth) {
        return;
      }
      if (props.size === "lg") {
        return `flex-direction: row !important;
     width: 100% !important;
     justify-content: space-evenly !important;`;
      }
      if (convertWidth(props.size) > props.screenwidth) {
        return;
      }
      if (props.size === "xl") {
        return `flex-direction: row !important;
     width: 100% !important;
     justify-content: space-evenly !important;`;
      }
      if (convertWidth(props.size) > props.screenwidth) {
        return;
      }
    }
  }}
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const DialogHeader = styled(`div`)`
  font-size: 26px;
  font-weight: 600;
  color: #151515;
  text-align: center;
  line-height: 133.4%;
`;
const DialogDescription = styled(`div`)`
  font-size: 16px;
  font-weight: 500;
  color: #707070;
  margin-top: 10px;
  word-break: break-word;
  display: flex;
  text-align: -webkit-center;
  justify-content: center;
  max-width: 480px;
  padding: 0px 24px;
`;
const ButtonDiv = styled(`div`)`
  padding-top: 20px;
  ${(props) => props.theme.breakpoints.only("xs")} {
    flex-direction: column-reverse;
    align-items: center;
  }
  display: flex;
  justify-content: center;
`;

const Banner = styled(`div`)<IVariant>`
  height: 6px;
  width: 80px;
  margin-bottom: 20px;
  border-radius: 40px;
  ${(props) => {
    if (props.variant === "error") {
      return `background: #FF2618;`;
    }
    if (props.variant === "warning") {
      return `background: #F4B000;`;
    } else {
      return `background: #2D9558;`;
    }
  }}
`;
const StyledButton = styled(Button)`
  ${(props) => props.theme.breakpoints.only("xs")} {
    margin: 6px 10px;
  }
  min-width: 150px;
  margin: 0px 10px;
  word-break: break-all;
`;

const DialogContext = React.createContext<IDialogContext>(undefined);

const DialogTemplate: React.FC<IDialogTemplateProps> = ({
  open,
  handleClose,
  title,
  description,
  children,
  variant,
  img,
  alt,
  size,
  isClose,
  ...props
}) => {
  return (
    <>
      <Dialog open={open} handleClose={handleClose} size={size} isClose={isClose} {...props}>
        <DialogContentDiv size={size} screenwidth={useWindowSize()}>
          {/* font-size 96 and color(if ด้วย error) */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: "25px",
            }}
          >
            <Banner variant={variant} />
            {img && <img src={img} alt={alt} width={200} height={200} />}
          </div>
          <div>
            {title && <DialogHeader>{title}</DialogHeader>}
            {description && <DialogDescription>{description}</DialogDescription>}
            {children}
          </div>
        </DialogContentDiv>
      </Dialog>
    </>
  );
};

const DialogInfo: React.FC<IDialogInfoProps> = ({
  isButton = false,
  confirmText,
  confirmButtonProps = {},
  onConfirmation,
  children,
  size,
  ...props
}) => {
  const ConvertVariant = (variant) => {
    if (variant === "error") {
      return { variant: "standard", color: "primary" };
    }
    if (variant === "standard") {
      return { variant: "contained", color: "primary" };
    }
    if (variant === "warning") {
      return { variant: "contained", color: "warning" };
    }
  };
  return (
    <>
      <DialogTemplate size={size} {...props}>
        {children}
        {isButton && (
          <ButtonDiv>
            <StyledButton
              onClick={onConfirmation}
              {...ConvertVariant(props.variant)}
              {...confirmButtonProps}
            >
              {confirmText}
            </StyledButton>
          </ButtonDiv>
        )}
      </DialogTemplate>
    </>
  );
};

const DialogConfirm: React.FC<IDialogConfirmProps> = ({
  confirmText = "yes",
  cancelText = "No",
  confirmButtonProps = {},
  cancelButtonProps = {},
  onConfirmation,
  onClose,
  children,
  size,
  ...props
}) => {
  const ConvertVariant = (variant) => {
    if (variant === "error") {
      return { variant: "contained", color: "error" };
    }
    if (variant === "standard") {
      return { variant: "contained", color: "primary" };
    }
    if (variant === "warning") {
      return { variant: "contained", color: "warning" };
    }
  };
  const [loading, setLoading] = useState(false);
  const handleOnConfirmation = async () => {
    setLoading(true);
    onConfirmation();
    setLoading(false);
  };
  return (
    <>
      <DialogTemplate size={size} {...props}>
        {children}
        <ButtonDiv>
          <StyledButton variant="standard" color="primary" onClick={onClose} {...cancelButtonProps}>
            {cancelText}
          </StyledButton>
          <StyledButton
            onClick={handleOnConfirmation}
            {...ConvertVariant(props.variant)}
            {...confirmButtonProps}
            disabled={loading}
          >
            {confirmText}
          </StyledButton>
        </ButtonDiv>
      </DialogTemplate>
    </>
  );
};

const DialogContinue: React.FC<IDialogConfirmProps> = ({
  confirmText = "Continue",
  cancelText = "Back",
  confirmButtonProps = {},
  cancelButtonProps = {},
  onConfirmation,
  onClose,
  children,
  size,
  ...props
}) => {
  const [loading, setLoading] = useState(false);
  const handleOnConfirmation = async () => {
    setLoading(true);
    onConfirmation();
    setLoading(false);
  };
  return (
    <>
      <DialogTemplate size={size} isClose={false} {...props}>
        {children}
        <Stack mt="24px" spacing={1.5}>
          <Button
            disabled={loading}
            onClick={handleOnConfirmation}
            variant="contained"
            color="error"
          >
            {confirmText}
          </Button>
          {/* <Button variant="outlined" color="neutral" onClick={() => onClose?.({}, "backdropClick")}> */}
          {/*   {cancelText} */}
          {/* </Button> */}
          <StyledButton variant="standard" color="primary" onClick={onClose} {...cancelButtonProps}>
            {cancelText}
          </StyledButton>
        </Stack>
      </DialogTemplate>
    </>
  );
};
export const DialogProvider = ({ children }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogConfig, setDialogConfig] = React.useState<IDialogConfig>({});
  const [typeDialog, setTypeDialog] = React.useState<ITypeDialog>("info");

  const openDialog = (type, { ...props }) => {
    setDialogConfig({});
    setDialogOpen(true);
    setTypeDialog(type);
    setDialogConfig({ ...props });
  };
  const resetDialog = () => {
    // setDialogConfig({}); bug dialog ขึ้นซ้อนกัน
    setDialogOpen(false);
  };

  const onConfirmation = async () => {
    await (dialogConfig.onSubmit && dialogConfig.onSubmit());
    resetDialog();
  };

  const onConfirmContinueWithoutQr = async () => {
    resetDialog();
    await (dialogConfig.onSubmit && dialogConfig.onSubmit());
  };

  const onCloseCross = () => {
    resetDialog();
  };

  const onClose = () => {
    dialogConfig.onCancel && dialogConfig.onCancel();
    resetDialog();
  };

  return (
    <DialogContext.Provider value={{ openDialog, onCloseCross }}>
      {typeDialog === "info" && (
        <DialogInfo
          open={dialogOpen}
          handleClose={onCloseCross}
          onConfirmation={onConfirmation}
          {...dialogConfig}
        >
          {dialogConfig.content}
        </DialogInfo>
      )}
      {typeDialog === "confirm" && (
        <DialogConfirm
          open={dialogOpen}
          handleClose={onCloseCross}
          onConfirmation={onConfirmation}
          onClose={onClose}
          {...dialogConfig}
        >
          {dialogConfig.content}
        </DialogConfirm>
      )}
      {typeDialog === "continue" && (
        <DialogContinue
          open={dialogOpen}
          handleClose={onCloseCross}
          onConfirmation={onConfirmContinueWithoutQr}
          onClose={onClose}
          {...dialogConfig}
        >
          {dialogConfig.content}
        </DialogContinue>
      )}
      {typeDialog === "blank" && (
        <Dialog open={dialogOpen} handleClose={onCloseCross}>
          {dialogConfig.content}
        </Dialog>
      )}
      {children}
    </DialogContext.Provider>
  );
};

export const useDialog = () => {
  const { openDialog, onCloseCross: onClose } = React.useContext(DialogContext);
  const { t } = useTranslation();
  const getDialog = useCallback(({ type = "info", ...props }: Omit<IGetDialog, "open">) => {
    openDialog(type, { ...props });
  }, []);

  const getCannotSomethingDialog = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "info",
      variant: "error",
      title: t("dialog.cannot_process"),
      description: t("dialog.please_try_again"),
      img: Failed,
      ...props,
    });
  };

  const getDialogCannotSave = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "info",
      variant: "error",
      title: t("dialog.cannot_save"),
      description: t("dialog.please_try_again"),
      img: SaveFailed,
      ...props,
    });
  };

  const getWantBackDialog = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "confirm",
      title: t("dialog.wantback"),
      variant: "warning",
      description: t("dialog.back_not_save_desc"),
      cancelText: t("back"),
      confirmText: t("dialog.stay_page"),
      img: Back,
      ...props,
    });
  };

  const getWantContinueNoQrDialog = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "continue",
      title: t("dialog.qrcode_not_found"),
      description: t("dialog.qrcode_desc"),
      variant: "error",
      cancelText: t("dialog.qr_back"),
      confirmText: t("dialog.continue_without_qr"),
      img: UndefinedQr,
      ...props,
    });
  };

  const getUseFunctionBetaDialog = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "confirm",
      title: t("dialog.use_function_beta"),
      variant: "standard",
      description: t("dialog.use_function_beta_description"),
      cancelText: t("not yet"),
      confirmText: t("get_started"),
      img: Lab,
      ...props,
    });
  };

  const getDisclaimerDialog = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "confirm",
      title: t("dialog.disclaimer_title"),
      variant: "standard",
      description: t("dialog.disclaimer_description"),
      cancelText: t("cancel"),
      confirmText: t("continue"),
      img: Share,
      ...props,
    });
  };

  const getDialogCannotDownload = (props: Omit<IGetDialog, "open">) => {
    getDialog({
      type: "info",
      variant: "error",
      img: LoadFailed,
      title: t("dialog.cannot_download"),
      description: t("dialog.please_try_again"),
      ...props,
    });
  };

  return {
    getDialog,
    onClose,
    getCannotSomethingDialog,
    getDialogCannotSave,
    getWantBackDialog,
    getUseFunctionBetaDialog,
    getDisclaimerDialog,
    getDialogCannotDownload,
    getWantContinueNoQrDialog,
  };
};
